
import { PortfolioData } from "@/pages/DigitalPortfolio";
import { Button } from "@/components/ui/button";
import { 
  Download, 
  Link2, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight,
  Maximize,
  Minimize
} from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

interface PortfolioPreviewProps {
  portfolio: PortfolioData;
}

export const PortfolioPreview = ({ portfolio }: PortfolioPreviewProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const fontClass = () => {
    switch (portfolio.font) {
      case "Roboto": return "font-sans";
      case "Playfair Display": return "font-serif";
      case "Montserrat": return "font-sans";
      case "Open Sans": return "font-sans";
      default: return "font-sans";
    }
  };

  // Function to determine text color based on background color brightness
  const getTextColor = (bgColor: string) => {
    // Convert hex to RGB
    const hex = bgColor.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return white for dark backgrounds, black for light
    return brightness > 125 ? '#000000' : '#FFFFFF';
  };

  // Empty state if no items
  if (portfolio.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-10 text-center">
        <h3 className="text-xl font-semibold mb-2">Your portfolio is empty</h3>
        <p className="text-gray-500 max-w-md mb-6">
          Add some items to your portfolio to preview how it will look.
        </p>
      </div>
    );
  }

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
      {/* Preview Toolbar */}
      <div className="border-b flex items-center justify-between p-3 bg-gray-50 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button variant="outline" size="sm">
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          Viewing as: <span className="font-medium">Visitor</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Link2 className="h-4 w-4 mr-1" />
            Copy Link
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize className="h-4 w-4" />
            ) : (
              <Maximize className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Portfolio Preview */}
      <div 
        className={`${fontClass()} p-8 overflow-y-auto`}
        style={{ 
          maxHeight: isFullscreen ? 'calc(100vh - 57px)' : '600px',
          color: getTextColor(portfolio.color)
        }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Portfolio Header */}
          <div 
            className="py-12 mb-8 text-center"
            style={{ backgroundColor: `${portfolio.color}10` }}
          >
            <h1 
              className="text-4xl font-bold mb-4"
              style={{ color: portfolio.color }}
            >
              {portfolio.title}
            </h1>
            <p className="text-lg max-w-2xl mx-auto">
              {portfolio.description}
            </p>
          </div>
          
          {/* Portfolio Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolio.items.map((item) => (
              <div key={item.id} className="group">
                {item.type === "image" && item.content && (
                  <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-4 relative group-hover:shadow-md transition-shadow">
                    <img 
                      src={item.content} 
                      alt={item.title || ""} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {item.type === "video" && (
                  <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-4 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center shadow-md">
                      <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-black border-b-8 border-b-transparent ml-1"></div>
                    </div>
                  </div>
                )}
                
                {item.type === "project" && (
                  <div 
                    className="aspect-video bg-gradient-to-br from-indigo-500/10 to-indigo-700/10 rounded-md overflow-hidden mb-4 p-6 flex flex-col items-center justify-center relative group-hover:shadow-md transition-shadow"
                    style={{ 
                      background: `linear-gradient(135deg, ${portfolio.color}10, ${portfolio.color}30)` 
                    }}
                  >
                    <h3 
                      className="text-2xl font-bold mb-2 text-center"
                      style={{ color: portfolio.color }}
                    >
                      {item.title}
                    </h3>
                    {item.link && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="mt-4 bg-white/80"
                      >
                        View Project <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </div>
                )}
                
                {item.type === "text" && (
                  <div className="prose max-w-none mb-4">
                    <h3 
                      className="text-xl font-bold mb-2"
                      style={{ color: portfolio.color }}
                    >
                      {item.title}
                    </h3>
                    <p className="whitespace-pre-line">
                      {item.content}
                    </p>
                  </div>
                )}
                
                {/* Common footer for all item types */}
                <div className="flex flex-col gap-2">
                  {item.type !== "text" && (
                    <h3 
                      className="text-xl font-bold"
                      style={{ color: portfolio.color }}
                    >
                      {item.title}
                    </h3>
                  )}
                  
                  {item.description && (
                    <p className="text-sm text-gray-600">
                      {item.description}
                    </p>
                  )}
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.tags.map(tag => (
                        <Badge 
                          key={tag} 
                          variant="secondary" 
                          className="text-xs"
                          style={{ 
                            backgroundColor: `${portfolio.color}15`,
                            color: portfolio.color 
                          }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Portfolio Footer */}
          <div className="mt-16 pt-8 border-t text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {portfolio.title} • Created with DGK Hub
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
